import { z } from "zod";
import type { QueryWarning } from "./query.ts";

export const VIEW_SCOPES = ["personal", "space", "product"] as const;
export const ViewScope = z.enum(VIEW_SCOPES);
export type ViewScope = z.infer<typeof ViewScope>;

export const VIEW_LAYOUTS = ["list", "board", "table", "timeline"] as const;
export const ViewLayout = z.enum(VIEW_LAYOUTS);
export type ViewLayout = z.infer<typeof ViewLayout>;

export const VIEW_DENSITIES = ["compact", "comfortable", "spacious"] as const;

/**
 * How a view is shown. Every key is optional and unknown keys are kept (the UI may store more, e.g.
 * collapsed groups), but the whole object may not exceed 16 KB.
 * `groupBy` / `subGroupBy`: none | status | priority | assignee | type | milestone | parent | label group slug.
 * `sort`: same syntax as the `?sort=` URL parameter / `sort:` clauses ("priority", "updated-asc", "rank").
 * `columns`: visible columns/properties, in order (table and list layouts).
 */
export const ViewDisplay = z
  .object({
    groupBy: z.string().max(64).nullish(),
    subGroupBy: z.string().max(64).nullish(),
    sort: z.string().max(200).nullish(),
    columns: z.array(z.string().max(64)).max(40).optional(),
    showSubItems: z.boolean().optional(),
    density: z.enum(VIEW_DENSITIES).optional(),
  })
  .loose()
  .refine((d) => JSON.stringify(d).length <= 16_384, "display is too large");
export type ViewDisplay = z.infer<typeof ViewDisplay>;

const Name = z.string().trim().min(1).max(120);
const Query = z.string().max(4_000);

export const ViewCreate = z
  .object({
    name: Name,
    scope: ViewScope.default("personal"),
    /** Space id (scope "space") or product id (scope "product"); must be null/omitted for personal views. */
    scopeId: z.uuid().nullish(),
    query: Query.default(""),
    layout: ViewLayout.default("list"),
    display: ViewDisplay.default({}),
    pinned: z.boolean().optional(),
  })
  .refine((v) => (v.scope === "personal") === (v.scopeId == null), {
    message: "scopeId is required for space and product views and not allowed for personal views",
    path: ["scopeId"],
  });
export type ViewCreate = z.input<typeof ViewCreate>;

/** Patch. Changing `scope`/`scopeId` moves (shares/unshares) the view; both must then be sent together. */
export const ViewPatch = z
  .object({
    name: Name.optional(),
    scope: ViewScope.optional(),
    scopeId: z.uuid().nullish(),
    query: Query.optional(),
    layout: ViewLayout.optional(),
    display: ViewDisplay.optional(),
  })
  .refine((v) => (v.scope === undefined) === (v.scopeId === undefined), {
    message: "scope and scopeId must be sent together",
    path: ["scopeId"],
  })
  .refine((v) => v.scope === undefined || (v.scope === "personal") === (v.scopeId == null), {
    message: "scopeId is required for space and product views and not allowed for personal views",
    path: ["scopeId"],
  });
export type ViewPatch = z.input<typeof ViewPatch>;

export const ViewReorder = z.object({ ids: z.array(z.string().min(1)).min(1).max(500) });
export type ViewReorder = z.infer<typeof ViewReorder>;

export const ViewPin = z.object({ pinned: z.boolean(), position: z.number().int().min(0).max(10_000).optional() });
export type ViewPin = z.infer<typeof ViewPin>;

export const ViewValidate = z.object({
  query: Query,
  scope: ViewScope.default("personal"),
  scopeId: z.uuid().nullish(),
});
export type ViewValidate = z.input<typeof ViewValidate>;

/** What the API returns for a view, saved or built-in. */
export interface ViewDto {
  /** Saved: uuid. Built-in: `builtin:<PRODUCT>:<slug>`. */
  id: string;
  builtin: boolean;
  /** Built-in only: all-open | board | next-up | triage | recently-closed. */
  slug: string | null;
  name: string;
  scope: ViewScope;
  scopeId: string | null;
  /** Personal views: the owner. Shared views: null. */
  ownerId: string | null;
  query: string;
  layout: ViewLayout;
  display: ViewDisplay;
  position: number;
  /** Pinned to the caller's sidebar. */
  pinned: boolean;
  pinPosition: number | null;
  /** The caller may rename, edit, reorder and delete this view. */
  canEdit: boolean;
  /**
   * Built-in "Next up": the order is the work queue (`GET /products/:key/queue`), which `query`
   * (`is:open -is:blocked`) only approximates. Clients should fetch the queue for ordering/membership.
   */
  queue: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  /** Present on create, update and validate responses. */
  warnings?: QueryWarning[];
}

export interface QueueEntry {
  key: string;
  id: string;
  /** Why it is in this position. */
  tier: "p0" | "review-finding" | "ranked";
}
